import React, { useLayoutEffect, useRef, useCallback } from 'react';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import type { CustomElement } from './slate-types';
import { sanitizeRichEmailHtml } from './sanitizeRichHtml';

type QuotedEl = CustomElement & { type: 'quoted-rich-html'; html: string };

interface QuotedRichHtmlElementProps {
  attributes: Record<string, unknown>;
  children: React.ReactNode;
  element: QuotedEl;
  editor: Parameters<typeof ReactEditor.findPath>[0];
}

/**
 * Unlocked reply/forward quoted body: keep original HTML + styles (like the iframe view),
 * with a contentEditable surface that syncs back into the Slate document.
 */
export function QuotedRichHtmlElement({ attributes, children, element, editor }: QuotedRichHtmlElementProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const html = element.html || '';
  const path = ReactEditor.findPath(editor, element);

  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return;
    if (document.activeElement === node) return;
    if (node.innerHTML !== html) {
      node.innerHTML = html;
    }
  }, [html]);

  const syncFromDom = useCallback(() => {
    const node = innerRef.current;
    if (!node) return;
    const next = sanitizeRichEmailHtml(node.innerHTML);
    if (next !== element.html) {
      Transforms.setNodes(editor, { html: next } as Partial<QuotedEl>, { at: path });
    }
  }, [editor, path, element.html]);

  return (
    <div
      {...attributes}
      contentEditable={false}
      data-slate-quoted-rich-wrap
      style={{ display: 'block', width: '100%', maxWidth: '100%' }}
    >
      <div
        ref={innerRef}
        contentEditable
        suppressContentEditableWarning
        className="email-quoted-block-inner email-quoted-rich-editable"
        style={{
          outline: 'none',
          fontSize: 14,
          lineHeight: 1.55,
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          color: 'inherit',
        }}
        onInput={syncFromDom}
        onBlur={syncFromDom}
      />
      {children}
    </div>
  );
}
